/**
 * Contenu bilingue du hub location et de ses sous-composants.
 *
 * FR : `/location` (URL d'origine inchangée). EN : `/en/location`.
 * Fournit le "chrome" localisé de HeroSection, InfoSection, VehiculeSection et
 * de la section "parcours" du hub (LocationHubView). Défaut `fr` → zéro
 * régression FR (les chaînes FR sont reprises verbatim).
 *
 * Liens transverses EN : pointent vers l'EN quand il existe (catalogue,
 * réservation), sinon FR (zéro 404).
 *
 * Consommé par : HeroSection, InfoSection, VehiculeSection, LocationHubView.
 */
import type { Locale } from "@/config/i18n";
import { type I18n } from "@/config/localeText";

/** Base de la page catalogue selon la locale (réutilisée par les CTA). */
export const vehiclesBasePath = (locale: Locale): string =>
  locale === "en" ? "/en/location/vehicules" : "/location/vehicules";

/** Base du hub location selon la locale (breadcrumb, self-link). */
export const locationHubBasePath = (locale: Locale): string =>
  locale === "en" ? "/en/location" : "/location";

export type HeroSectionChrome = {
  imageAlt: string;
  titleLead: string;
  titleRest: string;
  description: string;
  ctaFleet: string;
};

export const HERO_SECTION: I18n<HeroSectionChrome> = {
  fr: {
    imageAlt: "Location de véhicules premium sur la Côte d'Azur",
    titleLead: "Location",
    titleRest: " de véhicules premium pour vos déplacements d'exception",
    description:
      "KS Rent Services met à votre disposition une flotte haut de gamme et un service sur-mesure. Confiez-nous vos trajets professionnels, événements et transferts avec élégance et discrétion.",
    ctaFleet: "Découvrir notre flotte",
  },
  en: {
    imageAlt: "Premium vehicle rental on the French Riviera",
    titleLead: "Premium",
    titleRest: " vehicle rental for your exceptional journeys",
    description:
      "KS Rent Services provides a high-end fleet and a bespoke service. Entrust us with your business trips, events and transfers with elegance and discretion.",
    ctaFleet: "Discover our fleet",
  },
  ar: {
    imageAlt: "تأجير سيارات فاخرة على الريفييرا الفرنسية",
    titleLead: "تأجير",
    titleRest: " سيارات فاخرة لتنقّلاتك الاستثنائية",
    description:
      "تضع كي إس رنت سرفيسز بين يديك أسطولاً راقياً وخدمة مصمّمة على القياس. اعهد إلينا برحلات عملك ومناسباتك وتنقّلاتك بأناقة وتحفّظ.",
    ctaFleet: "اكتشف أسطولنا",
  },
};

export type InfoSectionChrome = {
  byAppointment: string;
  contactUs: string;
};

export const INFO_SECTION: I18n<InfoSectionChrome> = {
  fr: {
    byAppointment: "Accueil sur rendez-vous uniquement",
    contactUs: "Nous contacter",
  },
  en: {
    byAppointment: "By appointment only",
    contactUs: "Contact us",
  },
  ar: {
    byAppointment: "الاستقبال بموعد مسبق فقط",
    contactUs: "تواصل معنا",
  },
};

export type VehiculeSectionChrome = {
  heading: string;
  subheading: string;
  seeAll: string;
};

export const VEHICULE_SECTION: I18n<VehiculeSectionChrome> = {
  fr: {
    heading: "Nos véhicules à la une",
    subheading: "Découvrez une sélection de nos nouveaux véhicules d'exception",
    seeAll: "TOUT VOIR",
  },
  en: {
    heading: "Our featured vehicles",
    subheading: "Discover a selection of our latest exceptional vehicles",
    seeAll: "SEE ALL",
  },
  ar: {
    heading: "سياراتنا المميّزة",
    subheading: "اكتشف مجموعة مختارة من أحدث سياراتنا الاستثنائية",
    seeAll: "عرض الكل",
  },
};

export type HubLink = { label: string; href: string };

export type LocationHubContent = {
  crumbHome: string;
  crumbRental: string;
  parcoursHeading: string;
  parcoursIntro: string;
  parcoursLinks: HubLink[];
  fleetVisualAlt: string;
  processVisualAlt: string;
  faqHeading: string;
  faq: { q: string; a: string }[];
};

export const LOCATION_HUB: I18n<LocationHubContent> = {
  fr: {
    crumbHome: "Accueil",
    crumbRental: "Location",
    parcoursHeading: "Parcours location à explorer",
    parcoursIntro:
      "Ces pages vous aident à comparer les bonnes formules selon votre usage: prestige, courte durée, longue durée et offres en cours.",
    parcoursLinks: [
      { label: "Explorer la location prestige", href: "/location/prestige" },
      {
        label: "Comprendre la courte durée (1 à 7 jours)",
        href: "/location/courte-duree",
      },
      {
        label: "Structurer une location longue durée",
        href: "/location/longue-duree",
      },
      {
        label: "Voir les offres location disponibles",
        href: "/location/offres",
      },
    ],
    fleetVisualAlt: "Flotte location premium KS Rent Services",
    processVisualAlt: "Parcours de remise et restitution véhicule",
    faqHeading: "Questions fréquentes sur la location de voiture",
    faq: [
      {
        q: "Quels véhicules peut-on louer chez KS Rent Services ?",
        a: "Une flotte premium et prestige : citadines sportives, berlines, SUV et cabriolets d'exception, de la Mini John Cooper Works aux SUV et cabriolets de luxe. La sélection complète et les disponibilités figurent sur la page flotte, avec devis immédiat.",
      },
      {
        q: "Peut-on louer avec ou sans chauffeur ?",
        a: "Les deux. Vous conduisez vous-même en location courte ou longue durée, ou vous choisissez un véhicule avec chauffeur privé — notre service couvre Cannes, Nice, Monaco et toute la Côte d'Azur.",
      },
      {
        q: "Faut-il une caution et quelles pièces fournir ?",
        a: "Oui, une caution (empreinte ou dépôt de garantie) est demandée selon le véhicule, avec un permis de conduire en cours de validité et une pièce d'identité. Les conditions exactes sont précisées à la réservation.",
      },
      {
        q: "Livrez-vous le véhicule à l'aéroport de Nice ou à l'hôtel ?",
        a: "Oui, nous organisons la remise et la restitution à l'aéroport de Nice, à votre hôtel ou villa à Cannes et sur la Côte d'Azur, sur rendez-vous.",
      },
      {
        q: "Quelle est la durée minimale de location ?",
        a: "La location courte durée démarre à la journée (1 à 7 jours) ; pour un usage prolongé, la longue durée offre des conditions dédiées. Nous adaptons la formule à votre séjour.",
      },
      {
        q: "Comment obtenir un prix et réserver ?",
        a: "Indiquez vos dates, le véhicule souhaité et le lieu de remise : nous répondons avec un devis immédiat. KS Rent Services est noté 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  en: {
    crumbHome: "Home",
    crumbRental: "Rental",
    parcoursHeading: "Rental journeys to explore",
    parcoursIntro:
      "These pages help you compare the right options for your use: prestige, short-term, long-term and current offers.",
    parcoursLinks: [
      {
        label: "Explore prestige rental",
        href: "/en/location/prestige-car-rental",
      },
      {
        label: "Understand short-term (1 to 7 days)",
        href: "/en/location/short-term-car-rental",
      },
      {
        label: "Structure a long-term rental",
        href: "/en/location/long-term-car-rental",
      },
      {
        label: "See the available rental offers",
        href: "/en/location/rental-offers",
      },
    ],
    fleetVisualAlt: "Premium rental fleet KS Rent Services",
    processVisualAlt: "Vehicle handover and return journey",
    faqHeading: "Car rental — frequently asked questions",
    faq: [
      {
        q: "Which vehicles can I rent from KS Rent Services?",
        a: "A premium and prestige fleet: sporty city cars, saloons, SUVs and exceptional convertibles, from the Mini John Cooper Works to luxury SUVs and cabriolets. The full selection and availability are on the fleet page, with an instant quote.",
      },
      {
        q: "Can I rent with or without a chauffeur?",
        a: "Both. Drive yourself on a short- or long-term rental, or choose a chauffeur-driven car — our service covers Cannes, Nice, Monaco and the whole French Riviera.",
      },
      {
        q: "Is a deposit required and what documents do I need?",
        a: "Yes, a deposit (pre-authorisation or security deposit) applies depending on the vehicle, along with a valid driving licence and ID. The exact terms are confirmed at booking.",
      },
      {
        q: "Do you deliver the car to Nice airport or my hotel?",
        a: "Yes, we arrange handover and return at Nice airport, your hotel or villa in Cannes and across the Riviera, by appointment.",
      },
      {
        q: "What is the minimum rental duration?",
        a: "Short-term rental starts from one day (1 to 7 days); for longer use, long-term rental offers dedicated terms. We tailor the option to your stay.",
      },
      {
        q: "How do I get a price and book?",
        a: "Share your dates, the vehicle you want and the handover location: we reply with an instant quote. KS Rent Services is rated 5.0/5 on 38 Google reviews.",
      },
    ],
  },
  ar: {
    crumbHome: "الرئيسية",
    crumbRental: "التأجير",
    parcoursHeading: "مسارات التأجير لاستكشافها",
    parcoursIntro:
      "تساعدك هذه الصفحات على مقارنة الصيغ المناسبة حسب استخدامك: الفخامة، المدى القصير، المدى الطويل، والعروض السارية.",
    parcoursLinks: [
      {
        label: "استكشف التأجير الفاخر",
        href: "/ar/location/prestige",
      },
      {
        label: "افهم المدى القصير (من 1 إلى 7 أيام)",
        href: "/ar/location/courte-duree",
      },
      {
        label: "نظّم تأجيراً للمدى الطويل",
        href: "/ar/location/longue-duree",
      },
      {
        label: "اطّلع على عروض التأجير المتاحة",
        href: "/ar/location/offres",
      },
    ],
    fleetVisualAlt: "أسطول التأجير الفاخر لكي إس رنت سرفيسز",
    processVisualAlt: "مسار تسليم السيارة وإعادتها",
    faqHeading: "أسئلة شائعة حول تأجير السيارات",
    faq: [
      {
        q: "ما السيارات التي يمكن استئجارها من كي إس رنت سرفيسز؟",
        a: "أسطول فاخر ومتميّز: سيارات مدينة رياضية، سيدان، دفع رباعي، وسيارات مكشوفة استثنائية، من الميني جون كوبر ووركس إلى سيارات الدفع الرباعي والمكشوفة الفاخرة. تجد التشكيلة الكاملة والتوافر في صفحة الأسطول، مع عرض سعر فوري.",
      },
      {
        q: "هل يمكن الاستئجار مع سائق أو بدونه؟",
        a: "كلاهما. تقود بنفسك في التأجير القصير أو الطويل، أو تختار سيارة مع سائق خاص — تغطّي خدمتنا كان ونيس وموناكو وكامل الريفييرا الفرنسية.",
      },
      {
        q: "هل يلزم مبلغ تأمين وما الوثائق المطلوبة؟",
        a: "نعم، يُطلب تأمين (حجز مبلغ أو وديعة ضمان) بحسب السيارة، مع رخصة قيادة سارية وبطاقة هوية. تُحدَّد الشروط الدقيقة عند الحجز.",
      },
      {
        q: "هل توصّلون السيارة إلى مطار نيس أو الفندق؟",
        a: "نعم، ننظّم التسليم والإعادة في مطار نيس، أو في فندقك أو فيلّتك في كان وعلى امتداد الريفييرا، بموعد مسبق.",
      },
      {
        q: "ما الحد الأدنى لمدة التأجير؟",
        a: "يبدأ التأجير القصير من يوم واحد (من 1 إلى 7 أيام)؛ وللاستخدام الأطول يوفّر التأجير الطويل شروطاً مخصّصة. نكيّف الصيغة حسب إقامتك.",
      },
      {
        q: "كيف أحصل على السعر وأحجز؟",
        a: "أرسل تواريخك والسيارة المطلوبة ومكان التسليم: نردّ بعرض سعر فوري. كي إس رنت سرفيسز حاصلة على تقييم 5,0/5 من 38 مراجعة على Google.",
      },
    ],
  },
};

/**
 * SEO bilingue du hub location. Même forme que les "cityPairs" du
 * metadataRouter (`pair.fr.seo.canonical` / `pair.en.seo.canonical` +
 * `pair[locale].seo`), pour un câblage uniforme : FR `/location` ≠ EN
 * `/en/location` → hreflang réciproque + x-default FR.
 */
export const LOCATION_HUB_SEO: I18n<{
  seo: { title: string; description: string; canonical: string };
}> = {
  fr: {
    seo: {
      title:
        "Location de véhicules premium sur la Côte d'Azur | KS Rent Services",
      description:
        "Location de véhicules premium et prestige à Cannes, Nice et Monaco : courte durée, longue durée, offres et service sur-mesure avec ou sans chauffeur.",
      canonical: "/location",
    },
  },
  en: {
    seo: {
      title: "Premium vehicle rental on the French Riviera | KS Rent Services",
      description:
        "Premium and prestige vehicle rental in Cannes, Nice and Monaco: short-term, long-term, offers and a bespoke service with or without a chauffeur.",
      canonical: "/en/location",
    },
  },
  ar: {
    seo: {
      title: "تأجير سيارات فاخرة على الريفييرا الفرنسية | كي إس رنت سرفيسز",
      description:
        "تأجير سيارات فاخرة ومتميّزة في كان ونيس وموناكو: مدى قصير، مدى طويل، عروض، وخدمة مصمّمة على القياس مع سائق أو بدونه.",
      canonical: "/ar/location",
    },
  },
};
